import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  linkedSignal,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ClarityModule } from '@clr/angular';
import { ToastService } from 'src/app/services/toast.service';
import { Subject } from 'rxjs';

import { InTableValidatorDirective } from 'src/app/directives/validators/in-table-validator.directive';
import { BackendLocaleDatePipe } from 'src/app/pipes/get-locale-date.pipe';
import { MrTranslatePipe } from 'src/app/pipes/mr-translate.pipe';
import { ObjTypePipe as objtype } from 'src/app/pipes/objtype.pipe';
import { APIService } from 'src/app/services/APIService/api.service';
import { LinieDrehungService } from 'src/app/services/Shared/linie-drehung.service';
import { slideAnimation } from 'src/app/content/_components/detailview/slide.animation';
import { HistorieTreeComponent } from '../../detailview/historie-tree/historie-tree.component';
import { BauwerksDialogComponent } from '../bauwerksdialog/bauwerksdialog.component';
import { isMessEintrag, MangelDialogComponent } from '../mangelerfassungsdialog/mangeldialog.component';
import { SpezifikationsDialogComponent } from '../spezifikationsdialog/spezifikationsdialog.component';
import { StammdatenDialogComponent } from '../stammdatendialog/stammdatendialog.component';
import { AutofocusDirective } from 'src/app/directives/autofocus.directive';
import { ZaehlerStore } from '../../_tabs/zaehlertab/zaehler.store.service';
import { ZaehlertabComponent } from '../../_tabs/zaehlertab/zaehlertab.component';
import { MessungÜbersicht } from '../zaehlerverwaltung/zaehler.types';
import { MessungGridComponent } from '../../_tabs/messung-grid/messung-grid.component';
import { Table } from 'src/app/models/ui/table';
import { FileForPreview } from '../../_shared/preview-thumbnails/preview-thumbnail.type';
import { EntsperrAnlageService } from 'src/app/services/Shared/entsperr-anlage.service';
import { ConfirmService } from 'src/app/services/confirm.service';
import { NaturschutzdialogComponent } from '../naturschutzdialog/naturschutzdialog.component';
import { TabsInOverflowDirective } from 'src/app/directives/tabs-in-overflow.directive';
import { errorHandle } from 'src/app/misc/functions';
import { GrundinstandsetzungdialogComponent } from '../grundinstandsetzungdialog/grundinstandsetzungdialog.component';

@Component({
  selector: 'app-inspektionsdialog',
  templateUrl: './inspektionsdialog.component.html',
  styleUrls: ['./inspektionsdialog.component.scss'],
  imports: [
    ClarityModule,
    FormsModule,
    ReactiveFormsModule,
    SpezifikationsDialogComponent,
    StammdatenDialogComponent,
    MangelDialogComponent,
    BauwerksDialogComponent,
    HistorieTreeComponent,
    ZaehlertabComponent,
    MessungGridComponent,
    NaturschutzdialogComponent,
    InTableValidatorDirective,
    AutofocusDirective,
    TabsInOverflowDirective,
    MrTranslatePipe,
    BackendLocaleDatePipe,
    objtype,
    GrundinstandsetzungdialogComponent,
  ],
  providers: [ZaehlerStore, LinieDrehungService],
  animations: [slideAnimation],
  // changeDetection: ChangeDetectionStrategy.OnPush
})
export class InspektionsDialogComponent {
  private readonly apiService = inject(APIService);
  private readonly mrTranslate = inject(MrTranslatePipe);
  private readonly toastr = inject(ToastService);
  private readonly zaehlerStore = inject(ZaehlerStore);
  private readonly confirmService = inject(ConfirmService);
  protected readonly drehung = inject(LinieDrehungService);
  protected readonly localeDate = inject(BackendLocaleDatePipe);

  public readonly closedialog = new Subject<boolean>();
  private dataRefresh: Subject<void>;

  protected readonly mangelTab = viewChild(MangelDialogComponent);
  private readonly stammTab = viewChild(StammdatenDialogComponent);
  private readonly spezTab = viewChild(SpezifikationsDialogComponent);
  private readonly bauTab = viewChild(BauwerksDialogComponent);
  private readonly naturTab = viewChild(NaturschutzdialogComponent);
  private readonly grundInstTab = viewChild(GrundinstandsetzungdialogComponent);

  protected ostammid: number;
  protected oldOPRUEFID: number;
  protected otypid: number;
  protected kpruvid: number;
  protected typ_Line_Point: number;
  protected stammData: { startmeter: number; endmeter: number };
  protected messungen: Table<MessungÜbersicht>;
  protected skizzeName: string;
  protected messungsOPRUEFID: number;
  protected isDevEdit: boolean;
  protected objektbezeichung: boolean;

  set data(inData: any) {
    this.ostammid = inData.ostamm;
    this.typ_Line_Point = inData.type;
    this.otypid = inData.otypid;
    this.kpruvid = inData.kpruvid;
    this.stammData = inData.stammData;
    this.drehung.summe = this.stammData;
    this.isDevEdit = inData.isDevEdit;
    this.dataRefresh = inData.dataRefresh;
    this.oldOPRUEFID = inData.pruefid;
    this.fetchInspektion();
    this.objektbezeichung = inData.name;

    if (objtype.isPoint(this.typ_Line_Point)) {
      this.messungen = inData.messungen;
      this.skizzeName = inData.skizze;
      this.messungsOPRUEFID = inData.messungsOPRUEFID;
    }
  }

  protected readonly opruefid = signal<number>(undefined);
  protected readonly isMessungDrehen = signal(false);
  protected readonly datum = signal(BackendLocaleDatePipe.today);
  protected readonly isEdit = linkedSignal(() => this.isDevEdit);

  protected readonly checkListTitlesRs = this.apiService.getChecklistTitels(() =>
    objtype.isPoint(this.typ_Line_Point) ? this.otypid : undefined
  );
  protected readonly checkList = computed<string[]>(() =>
    this.checkListTitlesRs.hasValue() ? this.checkListTitlesRs.value() : []
  );

  protected readonly success = signal<Record<string, boolean>>({});

  protected readonly messVerlMeldung = signal<string>(undefined);
  protected readonly isMessVerlModalOpen = linkedSignal(() => this.messVerlMeldung() != null);

  protected readonly activeTab = signal<string>(undefined);
  protected readonly showChecklistenAuswahl = computed(() => this.activeTab() == 'Mangel');
  protected readonly assignedZaehlerName = this.zaehlerStore.assignedZaehlerName;

  private isSaved: boolean = false;
  protected readonly canDeleteMesswerteGleis = linkedSignal(() => this.typ_Line_Point == 4 && !this.isDevEdit);

  protected readonly showNaturschutz = this.apiService.getTypOption(
    () => (objtype.isLine(this.typ_Line_Point) ? this.otypid : undefined),
    'Opt_F_Naturschutz'
  );
  protected readonly showGrundInstand = this.apiService.getTypOption(() => this.otypid, 'Opt_I_Gundinst');

  constructor() {
    inject(EntsperrAnlageService).registerDestroyRef(inject(DestroyRef));

    effect(() => {
      const opruefid = this.opruefid();
      if (opruefid)
        untracked(() => {
          this.zaehlerStore.objekt.set({
            OPRUEFID: opruefid,
            OSTAMMID: this.ostammid,
          });
        });
    });
  }

  private async fetchInspektion() {
    try {
      const { OPRUEFID, Datum, isMessungDrehen } = await this.apiService.getOrCreateInspektion({
        OSTAMMID: this.ostammid,
        OPRUEFID: this.oldOPRUEFID,
        KPRUVID: this.kpruvid,
        OTYPID: this.otypid,
        isEdit: this.isDevEdit,
        typ_Line_Point: this.typ_Line_Point,
      });
      this.opruefid.set(OPRUEFID);
      this.datum.set(Datum);
      this.isMessungDrehen.set(isMessungDrehen);
      this.isEdit.set(OPRUEFID == this.oldOPRUEFID);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Inspektion konnte nicht erstellt werden'));
      this.closedialog.next(true);
    }
  }

  private async validateTabs(): Promise<boolean> {
    const success: Record<string, boolean> = {};
    success.stamm = await this.stammTab().saveIfValid();

    if (objtype.isPoint(this.typ_Line_Point)) {
      success.spez = await this.spezTab().saveIfValid();
      success.maengel = await this.mangelTab().checkPunktValid();
      if (this.assignedZaehlerName()) {
        success.zaehler = await this.zaehlerStore.onSave(this.opruefid());
      }
    } else {
      const { startmeter, endmeter } = (this.drehung.summe = this.stammTab().linieValues);
      success.spez = this.spezTab().isVollständig(startmeter, endmeter);
      success.mangel = this.mangelTab().checkLinieValid(startmeter, endmeter);
      success.bauwerke = this.bauTab().checkLinieValid(startmeter, endmeter);
      success.naturschutz = this.naturTab()?.checkLinieValid(startmeter, endmeter);
      success.grundInst = this.grundInstTab()?.checkLinieValid(startmeter, endmeter);
    }

    success.mangel = (await this.mangelTab().checkValid()) && success.mangel;

    this.success.set(success);
    return Object.values(success).every(res => res !== false);
  }

  protected async saveInspektion() {
    if ((await this.validateTabs()) == false) return;

    try {
      await this.apiService.saveInspektionsDetails({
        OPRUEFID: this.opruefid(),
        OSTAMMID: this.ostammid,
        KPRUVID: this.kpruvid,
        P_Datum: this.datum() || BackendLocaleDatePipe.today,
        M_Mess_Drehen: this.isMessungDrehen(),
      });
      this.isSaved = true;
      this.dataRefresh.next();
      this.toastr.success(this.mrTranslate.transform('Daten gespeichert'), this.mrTranslate.transform('Inspektion'));
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Inspektion'));
    }
  }

  protected async deletePruefung() {
    const text = 'Sind Sie sicher, dass Sie die aktuelle Inspektion löschen wollen?';
    const ok = await this.confirmService.danger(text);
    if (!ok) return;
    try {
      await this.apiService.setInspektionsDelStatus({
        OSTAMMID: this.ostammid,
        OPRUEFID: this.opruefid(),
        KPRUVID: this.kpruvid,
      });
      this.toastr.info(this.mrTranslate.transform('Daten wurden gelöscht'));
      this.dataRefresh.next();
      this.closedialog.next(true);
    } catch (er) {
      this.toastr.error(errorHandle(er), this.mrTranslate.transform('Inspektion'));
    }
  }

  protected async saveChecklist(titel: string) {
    try {
      await this.apiService.saveChecklist(titel, this.otypid, this.mangelTab().pruefarten);
      this.toastr.success(
        this.mrTranslate.transform('Checkliste wurde gespeichert'),
        this.mrTranslate.transform('Checkliste')
      );
      this.checkListTitlesRs.reload();
    } catch (ex) {
      this.toastr.error(errorHandle(ex), this.mrTranslate.transform('Checkliste'));
    }
  }

  protected async checkMessungsVerletzungen() {
    if (!this.messungen.rows?.length) {
      this.toastr.success(this.mrTranslate.transform('Es konnten keine Messungen zur Prüfung gefunden werden...'));
      return;
    }

    const mangel = this.mangelTab().mangelRows;
    const bezeichnungen = new Set<string>();
    let text = [];
    let innerHTML: string = '';

    this.messungen.rows.forEach((mess: MessungÜbersicht) => {
      if (!mess.Ausfall) return;
      const mBez = mess.Bezeichnung.toLowerCase();
      const ist = mess.Ist?.toString().replace(',', '.');
      const soll = mess.Soll?.toString().replace(',', '.');
      let richtung: '+' | '-';
      bezeichnungen.add(mBez);

      if (mBez.includes('zp1') || soll == '<=' || +ist > +soll) richtung = '+';
      else if (soll == '>=' || +ist < +soll) richtung = '-';

      if (!richtung) return;

      const row = mangel.find(row => row.Lage?.split(',').some((lage: string) => lage.toLowerCase().trim() == mBez));
      if (!row) text.push(mBez + ' --> ' + this.mrTranslate.transform('fehlt'));
      else if (!mBez.startsWith('ü') && !mBez.startsWith('zp') && this.isFalscheRichtung(row.Mangel, richtung))
        text.push(mBez + ' --> ' + this.mrTranslate.transform('falscher Mangeleintrag'));
    });

    innerHTML = text.join(' <br> ');
    text = [];

    mangel?.forEach((mngl: any) => {
      if (!mngl.mangel || !isMessEintrag(mngl.pruefart)) return;
      const isDokumentiert = mngl.lage?.split(',').some((lage: string) => bezeichnungen.has(lage.toLowerCase().trim()));
      if (!isDokumentiert) text.push(mngl.lage + ' --> ' + this.mrTranslate.transform('c'));
    });

    if (text.length)
      innerHTML +=
        ' <br><hr> ' + this.mrTranslate.transform('Fehler in der Dokumentation') + ':<hr> ' + text.join(' <br> ');

    if (innerHTML) this.messVerlMeldung.set(innerHTML);
    else this.toastr.success(this.mrTranslate.transform('Alle Messungsverletzungen sind korrekt eingetragen...'));
  }

  private isFalscheRichtung(mangel: string, richtung: '+' | '-') {
    switch (richtung) {
      case '+':
        return !(mangel.includes('+') || mangel.includes('groß') || mangel.includes('erweiter'));
      case '-':
        return !(mangel.includes('-') || mangel.includes('klein') || mangel.includes('vereng'));
    }
  }

  protected async deleteAlteMesswerteGleis() {
    const text = 'Sind Sie sicher, dass Sie die altem Messwerte löschen wollen?';
    const ok = await this.confirmService.danger(text);
    if (ok) {
      const success = await this.mangelTab()?.deleteAlteMesswerteGleis();
      this.canDeleteMesswerteGleis.set(!success);
    }
  }

  protected close() {
    if (!this.isDevEdit && this.isSaved == false) {
      this.toastr.error(this.mrTranslate.transform('Vor dem Beenden müssen Sie die Inspektion speichern...!'));
      return;
    }

    this.closedialog.next(true);
  }
}
